import type { SocialPlattform } from '../services/api';

/** Anzeigenamen der Plattformen. */
export const PLATTFORM_NAME: Record<SocialPlattform, string> = {
  instagram: 'Instagram',
  x: 'X',
  facebook: 'Facebook',
};

export const PLATTFORMEN: SocialPlattform[] = ['instagram', 'x', 'facebook'];

/** Harte Grenze je Tweet (Backend: social-text.ts). */
export const X_MAX_ZEICHEN = 280;
/** Ziel je Tweet im Prompt. */
export const X_ZIEL_ZEICHEN = 270;
export const IG_MAX_ZEICHEN = 2200;
export const FB_ZIEL = { min: 300, max: 1200 };
export const IG_ZIEL = { min: 600, max: 1500 };

const URL_ENDE = /[.,;:!?)\]}'"»“”]+$/;
const MIT_SCHEMA = /^(?:https?:\/\/|www\.)\S+$/i;
const NACKTE_DOMAIN = /^[a-z0-9-]+(?:\.[a-z0-9-]+)*\.(?:com|de|net|org|io|eu|ai|dev|app|co|info|biz|at|ch|tech|security|cloud)(?:\/\S*)?$/i;
const EMOJI = /\p{Extended_Pictographic}|\p{Regional_Indicator}/u;

function istUrl(wort: string): boolean {
  const kern = wort.replace(URL_ENDE, '');
  if (!kern || kern.includes('@')) return false;
  return MIT_SCHEMA.test(kern) || NACKTE_DOMAIN.test(kern);
}

/** Grapheme (mit Intl.Segmenter, sonst Code-Points). */
function grapheme(text: string): string[] {
  const Segmenter = (Intl as unknown as { Segmenter?: new (l: string, o: { granularity: string }) => { segment(t: string): Iterable<{ segment: string }> } }).Segmenter;
  if (Segmenter) return Array.from(new Segmenter('de', { granularity: 'grapheme' }).segment(text), (s) => s.segment);
  return Array.from(text);
}

function gewicht(text: string): number {
  let summe = 0;
  for (const g of grapheme(text)) {
    if (EMOJI.test(g)) {
      summe += 2;
      continue;
    }
    for (const z of Array.from(g)) {
      const cp = z.codePointAt(0) as number;
      const leicht = cp <= 4351 || (cp >= 8192 && cp <= 8205) || (cp >= 8208 && cp <= 8223) || (cp >= 8242 && cp <= 8247);
      summe += leicht ? 1 : 2;
    }
  }
  return summe;
}

/** Zeichen wie X sie zaehlt: gewichtet, jede URL 23 (wie das Backend). */
export function xZeichen(text: string): number {
  let summe = 0;
  for (const teil of (text || '').normalize('NFC').split(/(\s+)/)) {
    if (!teil) continue;
    if (!/\s/.test(teil) && istUrl(teil)) {
      const kern = teil.replace(URL_ENDE, '');
      summe += 23 + gewicht(teil.slice(kern.length));
      continue;
    }
    summe += gewicht(teil);
  }
  return summe;
}

/** Zeichen fuer Instagram/Facebook (Code-Points). */
export function zeichen(text: string): number {
  return Array.from(text || '').length;
}

export function enthaeltUrl(text: string): boolean {
  return (text || '').split(/\s+/).some((w) => w.length > 3 && istUrl(w));
}

/** Tweets eines gespeicherten Beitrags (Thread-JSON, sonst Text). */
export function tweetsVon(post: { text: string; thread?: string | null }): string[] {
  if (post.thread) {
    try {
      const liste = JSON.parse(post.thread);
      if (Array.isArray(liste)) {
        const tweets = liste.filter((t) => typeof t === 'string' && t.trim()).map((t: string) => t.trim());
        if (tweets.length > 0) return tweets;
      }
    } catch {
      // Text verwenden
    }
  }
  return post.text ? [post.text] : [];
}

/** Text, wie er gepostet wird (Instagram: Caption + Hashtags). */
export function gepostetVon(post: { platform: SocialPlattform; text: string; hashtags?: string | null; thread?: string | null }): string {
  if (post.platform === 'x') return tweetsVon(post).join('\n\n');
  if (post.platform === 'instagram' && post.hashtags) return `${post.text}\n\n${post.hashtags}`;
  return post.text;
}

/** "1,5 ct" bzw. "2,35 €" */
export function kostenText(cents: number | null | undefined): string {
  const wert = Number(cents) || 0;
  if (wert < 100) return `${wert.toLocaleString('de-DE', { maximumFractionDigits: 2 })} ct`;
  return `${(wert / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}
